import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import { usePrevious } from 'hooks';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { Formik, Form, Field } from 'formik';
import Odometer from 'assets/img/icons/gauge.svg';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { TelemetryActions } from 'state/modules/telemetry';

import { BaseModal } from 'components/UI';
import { TextField, MaterialSelectField } from 'components/FormFields';

const formSchema = Yup.object().shape({
  odometer: Yup.object().shape({
    magnitude: Yup.string().required('This field is required'),
    units: Yup.string().required('This field is required'),
  }),
});

const EditTelemetryModal = ({ open, telemetry, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchedUpdateTelemetry, fetchingUpdateTelemetry } = useSelector(
    (state) => state.telemetry
  );
  const prevFetched = usePrevious(fetchedUpdateTelemetry);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (fetchedUpdateTelemetry && fetchedUpdateTelemetry !== prevFetched && open) {
      handleClick();
      dispatch(TelemetryActions.resetUpdateTelemetry());
    }
  }, [fetchedUpdateTelemetry, prevFetched, open, handleClick, dispatch]);

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      title={i18n.t('telemetry.edit')}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      handleClick={handleClick}
      loading={fetchingUpdateTelemetry}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          odometer: {
            magnitude: telemetry?.odometer?.magnitude || '',
            units: telemetry?.odometer?.units || '',
          },
        }}
        onSubmit={(values) => {
          const vehicleId = telemetry?.vehicle.id;
          dispatch(TelemetryActions.fetchUpdateTelemetry(vehicleId, values));
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Box mr={1}>
                    <img src={Odometer} alt="odometer-icon" />
                  </Box>
                  <Typography variant="h3">
                    <Box fontSize={13} fontWeight="bold">
                      {i18n.t('telemetry.odometer')}
                    </Box>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Field
                  margin="none"
                  type="number"
                  label={i18n.t('telemetry.value')}
                  component={TextField}
                  name="odometer.magnitude"
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  type="text"
                  label={i18n.t('telemetry.units')}
                  name="odometer.units"
                  style={{ marginTop: 14 }}
                  component={MaterialSelectField}
                  options={[
                    { value: 'mi', label: i18n.t('telemetry.mi') },
                    { value: 'km', label: i18n.t('telemetry.km') },
                  ]}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

EditTelemetryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

EditTelemetryModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default EditTelemetryModal;
